/* Campos de UM idioma de uma versão legal (ADR-0774): título + corpo em markdown.

   O corpo tem pré-visualização porque o texto semeado abre com um GFM alert
   (`> [!WARNING]` avisando que é rascunho estrutural pendente de revisão jurídica) e o operador
   precisa VER que o aviso está lá antes de publicar — num textarea ele é só um blockquote com
   colchetes. O `Markdown` de @planuze/ui é o mesmo renderer que o app usa, então o que aparece aqui
   é o que o usuário final vê.

   Não existe campo de versão, status ou vigência: versão é do servidor e status/vigência mudam pelo
   publish. Este componente só conhece texto. */
import { Input, Label, Markdown, Textarea } from '@planuze/ui';
import { useLegalAdminContent } from '../../content/sections/legal-admin.js';

interface Props {
  readonly locale: string;
  readonly title: string;
  readonly content: string;
  /** Versão publicada é imutável — os campos viram leitura, sem botão que prometa salvar. */
  readonly readOnly: boolean;
  readonly preview: boolean;
  readonly onChange: (patch: { readonly title?: string; readonly content?: string }) => void;
}

export function LegalLocaleFields({ locale, title, content, readOnly, preview, onChange }: Props) {
  const copy = useLegalAdminContent().localeFields;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`legal-title-${locale}`}>{copy.titleLabel}</Label>
        <Input
          id={`legal-title-${locale}`}
          value={title}
          disabled={readOnly}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder={copy.titlePlaceholder}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`legal-content-${locale}`}>{copy.contentLabel}</Label>
        {preview ? (
          <section
            aria-label={copy.previewAriaLabel(locale)}
            className="max-h-80 overflow-y-auto rounded-md border border-border bg-muted/30 p-3"
          >
            {content.trim() === '' ? (
              <p className="text-sm text-muted-foreground">{copy.emptyPreview}</p>
            ) : (
              <Markdown>{content}</Markdown>
            )}
          </section>
        ) : (
          <Textarea
            id={`legal-content-${locale}`}
            value={content}
            disabled={readOnly}
            onChange={(e) => onChange({ content: e.target.value })}
            rows={14}
            className="font-mono text-xs"
            placeholder={copy.contentPlaceholder}
          />
        )}
      </div>
    </div>
  );
}
